import React, { useCallback, useRef, useState, useMemo } from 'react';
import CodeMirror, { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';
import { EditorView } from '@codemirror/view';
import { vim } from '@replit/codemirror-vim';
import { Code2, Info } from 'lucide-react';
import CustomTooltip from '../../../../components/CustomTooltip/CustomTooltip';
import { Button } from '../../../../components/ui/button';
import { Label } from '../../../../components/ui/label';
import VariableDropdown from '../../../../components/RichTextEditor/Toolbar/VariableDropdown/VariableDropdown';
import IntentSlot from '../../../../types/BotBuilder/IntentSlot';
import Slot from '../../../../types/BotBuilder/Slot';
import { SystemVariableDto } from '../../../../services/SystemVariableService/SystemVariableService';

interface CodeEditorProps {
  code: string;
  onChange: (code: string) => void;
  intentSlots?: IntentSlot[];
  globalSlots?: Slot[];
  systemVariables: SystemVariableDto[];
}

function CodeEditor({
  code,
  onChange,
  intentSlots = [],
  globalSlots = [],
  systemVariables
}: Readonly<CodeEditorProps>): React.ReactElement {
  const editorRef = useRef<ReactCodeMirrorRef>(null);
  const [vimMode, setVimMode] = useState(false);

  const handleVariableSelect = useCallback(
    (varName: string, isSystem: boolean, isGlobal?: boolean) => {
      let variableTemplate: string;
      if (isSystem) {
        variableTemplate = `event['system']['${varName}']`;
      } else if (isGlobal) {
        variableTemplate = `event['global_slots']['${varName}']`;
      } else {
        variableTemplate = `event['intent_slots']['${varName}']`;
      }

      const view = editorRef.current?.view;
      if (view) {
        const cursor = view.state.selection.main.head;
        view.dispatch({
          changes: { from: cursor, insert: variableTemplate },
          selection: { anchor: cursor + variableTemplate.length }
        });
        view.focus();
      } else {
        onChange(code + variableTemplate);
      }
    },
    [code, onChange]
  );

  const editorTheme = EditorView.theme({
    '.cm-content': { cursor: 'text', minHeight: '142px' },
    '.cm-line': { cursor: 'text' },
    '.cm-tooltip-autocomplete': { zIndex: 9999 }
  });

  const tooltipConfig = EditorView.baseTheme({
    '.cm-tooltip': { zIndex: 9999 }
  });

  const extensions = useMemo(() => {
    const base = [
      python(),
      editorTheme,
      tooltipConfig,
      EditorView.lineWrapping
    ];
    return vimMode ? [vim(), ...base] : base;
  }, [vimMode, editorTheme, tooltipConfig]);

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center justify-between w-full'>
        <div className='flex items-center gap-1 shrink-0'>
          <Label className='text-sm font-medium'>Python code</Label>
          <CustomTooltip
            content={
              <>
                <p className='mb-1'>Return a dict to set output variables:</p>
                <code className='text-green-300'>
                  return {'{'}&apos;result&apos;: value{'}'}
                </code>
              </>
            }
            sideOffset={5}
          >
            <button type='button' className='inline-flex'>
              <Info className='w-4 h-4 text-gray-400 cursor-help' />
            </button>
          </CustomTooltip>
        </div>
        <div className='flex items-center gap-2 shrink-0'>
          <Button
            variant={vimMode ? 'default' : 'outline'}
            size='sm'
            onClick={() => {
              setVimMode(!vimMode);
              setTimeout(() => editorRef.current?.view?.focus(), 0);
            }}
            className='h-8 px-2 text-xs font-mono'
          >
            VIM
          </Button>
          <VariableDropdown
            intentSlots={intentSlots}
            globalSlots={globalSlots}
            systemVariables={systemVariables}
            onSelect={handleVariableSelect}
            triggerClassName='h-8 px-2 text-xs'
            triggerIcon={<Code2 className='h-4 w-4' />}
            title='Insert variable'
          />
        </div>
      </div>
      <div className='border rounded-lg bg-white'>
        <CodeMirror
          ref={editorRef}
          value={code}
          onChange={onChange}
          extensions={extensions}
          placeholder='# Enter your Python code here...'
          minHeight='150px'
          basicSetup={{
            lineNumbers: true,
            foldGutter: false,
            highlightActiveLine: true,
            indentOnInput: true
          }}
        />
      </div>
      <p className='text-xs text-muted-foreground'>
        Access variables via <code>event</code> (e.g.,{' '}
        <code>event[&apos;intent_slots&apos;][&apos;name&apos;]</code>). Return
        a dict to set output variables.
      </p>
    </div>
  );
}

export default CodeEditor;
